import React from 'react';
import PropTypes from 'prop-types';
import { isInternetExplorer } from '../../common/utils/browser';
import { memoize, isNumber, orderBy } from 'lodash';
import { getResizedImageUrl } from '../../common/utils/get-resized-image-url';
import classnames from 'classnames';
import styles from './picture.scss';
import { PictureMode } from './picture.types';
const memoizeMap = (func) => {
    const memoizedFunc = memoize(func, ({ url, min, max, width, height }) => [url, min, max, width, height].join('-'));
    memoizedFunc.cache = new Map();
    return memoizedFunc;
};
export class Picture extends React.Component {
    constructor(props) {
        super(props);
        this.getSrc = (url, width, height) => {
            if (!url) {
                return '';
            }
            const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio ?? 1 : 1;
            const defaultRatios = [1, 2, 3];
            const pixelRatios = Array.from(new Set([...defaultRatios, devicePixelRatio]));
            return Array.from(pixelRatios
                .reduce((mappedUrls, ratio) => {
                const resizedUrl = getResizedImageUrl({
                    url,
                    width: width * ratio,
                    height: height * ratio,
                });
                const ratiosPerUrl = mappedUrls.get(resizedUrl);
                if (ratiosPerUrl) {
                    ratiosPerUrl.push(ratio);
                }
                else {
                    mappedUrls.set(resizedUrl, [ratio]);
                }
                return mappedUrls;
            }, new Map())
                .entries())
                .map(([resizedUrl, ratios], _, allUrls) => allUrls.length > 1 ? `${resizedUrl} ${ratios[0]}x` : resizedUrl)
                .join(', ');
        };
        this.getBreakpointSource = memoizeMap(({ url, min, max, width, height }) => {
            const src = this.getSrc(url, width, height);
            let media = '';
            if (isNumber(min)) {
                media += `(min-width: ${min}px)`;
            }
            if (isNumber(max)) {
                if (media) {
                    media += ' and ';
                }
                media += `(max-width: ${max}px)`;
            }
            return { media, src };
        });
        this.state = {
            isIE: false,
            isClient: false,
        };
    }
    componentDidMount() {
        this.setState({
            isIE: isInternetExplorer(),
            isClient: true,
        });
    }
    getDefaultBackgroundStyle({ fallbackToBackgroundImage, sources, }) {
        const defaultSrc = fallbackToBackgroundImage
            ? sources[sources.length - 1].src
            : sources[0].src;
        const imageSet = defaultSrc
            .split(', ')
            .map((src) => {
            const match = src.match(/^(.+?)(?: (\d(?:\.\d+)?)x)?$/);
            if (!match) {
                return `url("${src}")`;
            }
            const [, url, ratio = '1'] = match;
            return `url("${url}") ${ratio}x`;
        })
            .join(', ');
        const style = {
            backgroundImage: defaultSrc.includes(', ')
                ? `-webkit-image-set(${imageSet})`
                : `url("${defaultSrc}")`,
        };
        return { style, defaultSrc };
    }
    render() {
        const { src, breakpoints, dataHook, children, className, alt, mode, withFallbackColor, highPriority = false, } = this.props;
        const { isIE, isClient } = this.state;
        const orderedBreakpoints = orderBy(breakpoints, ({ width, height }) => width * height);
        const sources = orderedBreakpoints.map((breakpoint) => this.getBreakpointSource({ url: src, ...breakpoint }));
        // IE does not support object-fit css property, so we fallback to backgroundImage.
        // But we still render img tag for SEO
        const fallbackToBackgroundImage = isIE && mode === PictureMode.COVER;
        const { style, defaultSrc } = this.getDefaultBackgroundStyle({
            fallbackToBackgroundImage,
            sources,
        });
        return (React.createElement("div", { className: classnames(styles.pictureContainer, className, {
                [styles.contain]: mode === PictureMode.CONTAIN,
                [styles.cover]: mode === PictureMode.COVER,
                [styles.fitToWidth]: mode === PictureMode.FIT_TO_WIDTH,
                [styles.fitToHeight]: mode === PictureMode.FIT_TO_HEIGHT,
                [styles.withFallbackColor]: withFallbackColor,
            }), "data-hook": dataHook, style: style },
            React.createElement("picture", { className: styles.picture, key: isClient ? 'client' : undefined },
                sources
                    .filter((source) => source.src)
                    .map((source) => (React.createElement("source", { media: source.media, srcSet: source.src, key: source.src }))),
                defaultSrc && (React.createElement("img", { alt: alt, className: styles.img, src: defaultSrc, rel: highPriority ? 'preload' : undefined, fetchPriority: highPriority ? 'high' : undefined }))),
            fallbackToBackgroundImage && (React.createElement("div", { className: styles.backgroundImage, style: style, "data-hook": "picture-background-image" })),
            React.createElement("div", { className: styles.content }, children)));
    }
}
Picture.propTypes = {
    alt: PropTypes.string,
    breakpoints: PropTypes.array,
    children: PropTypes.node,
    className: PropTypes.string,
    dataHook: PropTypes.string,
    src: PropTypes.string.isRequired,
    mode: PropTypes.oneOf([
        PictureMode.CONTAIN,
        PictureMode.COVER,
        PictureMode.FIT_TO_HEIGHT,
        PictureMode.FIT_TO_WIDTH,
    ]),
    withFallbackColor: PropTypes.bool,
};
Picture.defaultProps = {
    mode: PictureMode.COVER,
    breakpoints: [
        {
            min: 0,
            width: 320,
            height: 180,
        },
    ],
};
//# sourceMappingURL=picture.js.map